import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiClient } from '../../../auth-lib';
import {
  Button, Card, Select, StatusPill, Tabs, EmptyState, useToast,
} from '../../../design-system';
import TallyLedgers from './TallyLedgers';
import TallyEntries from './TallyEntries';
import TallyReports from './TallyReports';
import TallyMatching from './TallyMatching';
import TallyPushClients from './TallyPushClients';
import TallySyncActivity from './TallySyncActivity';
import TallyLedgerScreen from './TallyLedgerScreen';
import { dayTime } from '../../../lib/tallyBooks';
import useScrollRestore from '../../../lib/useScrollRestore';
import '../../styles/TallyPanel.css';

// Every connected company's books, at the firm's level.
//
// ⚠️ WHY THIS IS HERE RATHER THAN ON THE CLIENT. These four views — the chart
// of accounts, the entries, the reports Tally computes and the party-matching
// queue — used to live only inside a client record, one client at a time. Every
// one of them is something a practice does across its whole book: you browse
// ledgers when you are hunting for something, you read entries when you are
// reconciling a quarter, you clear a match queue in a single sitting. Doing any
// of that meant opening a client, working, going back, opening the next client
// and choosing the same view again. The company picker at the top of this
// screen is that whole loop, deleted.
//
// The client's own Tally tab keeps the one thing that IS a per-client question:
// what shape is this client in. See components/tally/TallyPosition.jsx.
//
// ⚠️ THE PICKER NAMES THE CLIENT, NOT JUST THE TALLY COMPANY. A firm routinely
// names a company differently from the client it belongs to — the review screen
// on the other tab warns about exactly that — so a list of bare Tally company
// names is unusable the moment two of them look alike. `clientName` comes off
// the connections endpoint for this.
//
// ⚠️ FRESHNESS TRAVELS WITH THE COMPANY, and it has to be re-stated here rather
// than assumed. On the client tab there is one connection and its age is in the
// header above whatever you read. Here you switch companies, and the company
// you have just moved to may not have synced in a week while the last one
// synced this morning.

const VIEWS = [
  // ⚠️ SYNC LEADS, because syncing is manual now. Nothing on the four views
  // behind it is current until somebody presses the button, and burying the
  // button that does it three tabs deep is how a firm reads last week's figures
  // believing they are today's.
  { key: 'sync', label: 'Sync' },
  { key: 'ledgers', label: 'Ledgers' },
  { key: 'entries', label: 'Entries' },
  { key: 'reports', label: 'Reports' },
  { key: 'matching', label: 'Client matching' },
  // ⚠️ THE OTHER END OF 'matching', AND IT SITS BESIDE IT FOR THAT REASON.
  // Matching asks which parties in these books are clients of the firm; this
  // asks which clients of the firm are missing from these books. Same
  // reconciliation, and it was only ever readable from one end.
  { key: 'push', label: 'Send to Tally' },
];

function freshness(conn) {
  if (!conn) return null;
  if (!conn.lastSuccessfulSyncAt) return { variant: 'warning', text: 'Never synced' };
  const h = conn.ageHours;
  if (h < 1) return { variant: 'success', text: 'Synced within the hour' };
  if (h < 24) return { variant: 'success', text: `Synced ${h}h ago` };
  const days = Math.floor(h / 24);
  return {
    variant: conn.stale ? 'danger' : 'warning',
    text: `Synced ${days} day${days === 1 ? '' : 's'} ago`,
  };
}

/** How a company reads in the picker: the client first, the Tally name after. */
function label(c) {
  const client = `${c.clientCode || ''} ${c.clientName || ''}`.trim();
  if (!client) return c.companyName;
  // ⚠️ BOTH NAMES, ALWAYS, even when they are identical. Showing the Tally name
  // only when it differs would make the differing rows — the ones worth a second
  // look — indistinguishable from a rendering quirk.
  return `${client} — ${c.companyName}`;
}

function TallyBooks() {
  const toast = useToast();
  // ⚠️ THE CHOSEN COMPANY LIVES IN THE URL so a client's own Tally tab can send
  // somebody straight to that company's books. It is also what makes the
  // selection survive a refresh and worth copying to a colleague — "open the
  // Books tab and find IDC PRO" is not a link.
  const [params, setParams] = useSearchParams();
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectionId, setConnectionId] = useState(() => params.get('company') || '');
  // ⚠️ THE VIEW IS IN THE URL, so the firm-wide Sync status panel on the
  // Companies tab can send somebody straight to the queue of the company that
  // is stuck — 'open the Books tab, find IDC PRO, click Sync' is not a link.
  // An unrecognised ?view= falls back rather than rendering nothing.
  const rawView = params.get('view');
  const [view, setViewState] = useState(
    () => (VIEWS.some((v) => v.key === rawView) ? rawView : 'ledgers'),
  );
  const setView = (next) => {
    setViewState(next);
    const q = new URLSearchParams(window.location.search);
    q.set('view', next);
    setParams(q, { replace: true });
  };
  const [busy, setBusy] = useState(false);
  // ⚠️ THE DRILL-DOWN IS A VIEW OF THIS SCREEN, NOT A DIALOG OVER IT. A ledger
  // account is nineteen columns and thousands of rows — a document, not a
  // question — and a modal turns it into a letterbox with two scrollbars.
  const [openLedgerId, setOpenLedgerId] = useState(null);
  const scroll = useScrollRestore();

  const openLedger = (id) => { scroll.save(); setOpenLedgerId(id); };
  const closeLedger = () => { setOpenLedgerId(null); scroll.restore(); };

  /**
   * Choose a company, and keep the URL saying so.
   *
   * ⚠️ THE STATE IS THE AUTHORITY AND THE URL MIRRORS IT, not the other way
   * round. Deriving the selection from the query on every render would make the
   * picker wait a navigation to respond, and would fight the fallback in `load`
   * that drops an id the server did not return.
   */
  const pickCompany = (id) => {
    setConnectionId(id);
    // Switching company while looking at a ledger would leave that account
    // open under a different company's name.
    setOpenLedgerId(null);
    const next = new URLSearchParams(params);
    if (id) next.set('company', id); else next.delete('company');
    setParams(next, { replace: true });
  };

  const load = useCallback(async () => {
    try {
      // No clientId: every connection the caller is allowed to see. The server
      // drops the ones behind a per-employee client blocklist, so a restricted
      // preparer's picker is short rather than full of 403s.
      const res = await apiClient.get('/api/tally/connections');
      const list = res.data || [];
      setConnections(list);
      // One connected company is the ordinary case for a firm starting out;
      // making them pick it out of a list of one is a step that exists only
      // because the code found it easier.
      //
      // ⚠️ AN ID THAT IS NOT IN THE LIST IS DROPPED, NOT TRUSTED. A deep link
      // can name a connection that has since been revoked, or one belonging to
      // a client this reader is blocked from — the server simply does not
      // return those, and keeping the id would leave the picker showing nothing
      // chosen while every panel below it fetched a 404.
      setConnectionId((prev) => {
        if (prev && list.some((c) => c.id === prev)) return prev;
        if (prev) toast.warning('That Tally company is no longer available to you.');
        return list.length === 1 ? list[0].id : '';
      });
    } catch {
      toast.danger('Could not list the connected Tally companies');
    } finally {
      setLoading(false);
    }
    // toast is stable enough for this load; re-running on it would refetch the
    // company list on every toast.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const connection = useMemo(
    () => connections.find((c) => c.id === connectionId) || null,
    [connections, connectionId],
  );
  const fresh = freshness(connection);

  /**
   * ⚠️ IT MOVES THE READER TO THE SYNC TAB, and that is the point of pressing it
   * here. Syncing is manual now, so somebody who presses this is waiting on it —
   * and a toast that vanishes in four seconds was the only feedback there was.
   * The Sync tab shows the queue, the machine and any failure.
   */
  const syncNow = async () => {
    setBusy(true);
    try {
      await apiClient.post(`/api/tally/connections/${connection.id}/sync`);
      setView('sync');
      await load();
    } catch (e) {
      const body = e.response?.data;
      // Already running is information, not a failure — the server says how long
      // it has been and that a dead one gives up by itself. Either way the Sync
      // tab is where the answer is, so go there.
      if (body?.code === 'TALLY_SYNC_RUNNING') {
        toast.warning(body.error);
        setView('sync');
      } else {
        toast.danger(body?.error || 'Could not start a sync');
      }
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Card><p className="tally-muted">Loading…</p></Card>;

  if (!connections.length) {
    return (
      <EmptyState
        icon="📗"
        title="No Tally company is connected yet"
        description="Scan a computer on the Companies tab, then connect a company to the client it belongs to. Its books appear here."
      />
    );
  }

  // ⚠️ RENDERED INSTEAD OF THE BOOKS, NOT ALONGSIDE THEM. Leaving the company
  // picker and the tab strip above a ledger account offers controls that would
  // change what is underneath the screen you are reading — switch company while
  // looking at a party and the two no longer describe the same books.
  if (openLedgerId && connection) {
    return (
      <TallyLedgerScreen
        connectionId={connection.id}
        ledgerId={openLedgerId}
        companyLabel={label(connection)}
        onBack={closeLedger}
      />
    );
  }

  return (
    <>
      <Card>
        <div className="tally-head">
          <div className="tally-books-pick">
            <Select
              label="Company"
              value={connectionId}
              onChange={(e) => pickCompany(e.target.value)}
            >
              <option value="">Choose a company…</option>
              {connections.map((c) => (
                <option key={c.id} value={c.id}>{label(c)}</option>
              ))}
            </Select>
          </div>

          {connection && (
            <div className="tally-actions">
              <StatusPill variant={fresh.variant} label={fresh.text} />
              {connection.status !== 'active' && (
                <StatusPill variant="warning" label={connection.status} />
              )}
              <Button
                variant="secondary"
                onClick={syncNow}
                disabled={busy || connection.status !== 'active'}
              >
                Sync now
              </Button>
            </div>
          )}
        </div>

        {connection && (
          <p className="tally-muted">
            {connection.lastSuccessfulSyncAt
              ? `Last read from Tally ${dayTime(connection.lastSuccessfulSyncAt)}. Everything below is a mirror of that moment.`
              : 'These books have never synced. Nothing below will show until one succeeds.'}
          </p>
        )}

        {/* Verbatim, as everywhere else in this module: "Tally is not running"
            and "no company is open" are different problems with different
            fixes, and collapsing them into "sync failed" turns a self-service
            correction into a support call. */}
        {connection?.lastError && (
          <p className="tally-error">
            <strong>Last attempt failed:</strong> {connection.lastError}
            {connection.consecutiveFailures > 1 && ` (${connection.consecutiveFailures} times in a row)`}
          </p>
        )}

        {connection?.stale && !connection.lastError && (
          <p className="tally-warn">
            These books have not refreshed recently. Anything read from them may be out of date.
          </p>
        )}
      </Card>

      {!connection ? (
        <EmptyState
          icon="📒"
          title="Pick a company"
          description="Choose one above to read its ledgers, its entries and the reports Tally computes for it."
        />
      ) : (
        <>
          <Tabs
            active={view}
            onChange={setView}
            layoutId="tally-books-tabs"
            tabs={VIEWS.map((v) => (v.key === 'matching'
              // Undefined rather than 0: Tabs renders the count whenever it is
              // defined, and a permanent grey zero turns a badge that should
              // mean "look at this" into furniture.
              ? { ...v, count: connection.pendingMatches || undefined }
              : v))}
          />

          {/* ⚠️ KEYED ON THE CONNECTION so switching company REMOUNTS the view.
              These components load on mount and hold their own filters; without
              the key, moving from one company to the next would leave the
              previous company's ledgers, search text and date range on screen
              under the new company's name — figures attributed to the wrong
              client, which is the exact failure this whole module is built
              against. */}
          {view === 'ledgers' && (
            <TallyLedgers
              key={connection.id}
              connectionId={connection.id}
              onOpenLedger={openLedger}
            />
          )}
          {view === 'entries' && <TallyEntries key={connection.id} connectionId={connection.id} />}
          {view === 'reports' && (
            <TallyReports
              key={connection.id}
              connectionId={connection.id}
              connectionStatus={connection.status}
            />
          )}
          {/* Confirming a match changes the count on the tab beside it, so the
              connection list reloads when one is decided. */}
          {view === 'matching' && (
            <TallyMatching
              key={connection.id}
              connection={connection}
              onChanged={load}
              onOpenLedger={openLedger}
            />
          )}
          {/* Switching writes on, and pushing, both change what the connection
              list says about this company — so it reloads on either. */}
          {view === 'push' && (
            <TallyPushClients
              key={connection.id}
              connection={connection}
              onChanged={load}
            />
          )}
          {view === 'sync' && (
            <TallySyncActivity
              key={connection.id}
              connection={connection}
              onChanged={load}
            />
          )}
        </>
      )}
    </>
  );
}

export default TallyBooks;
